/**
 * About/Summary Card Component
 * Displays and allows editing of professional summary
 * Matches design style from My Profile page
 *
 * NOTE: Per Redesigned_embedding_matching_strategy.md,
 * editing summary triggers Summary embedding regeneration
 */

import React, { useState, useEffect } from 'react';
import { FileText, PenSquare, X, Check, Sparkles, Loader2 } from 'lucide-react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { useDirtyState } from '../../hooks/useDirtyState';

const DAVID_URL = import.meta.env.VITE_DAVID_CANDIDATE_URL || 'http://localhost:8005';

interface AboutCardProps {
    profile: CandidateProfile;
    onSave?: (summary: string) => Promise<void>;
}

const MAX_SUMMARY_LENGTH = 500;

const AboutCard: React.FC<AboutCardProps> = ({ profile, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [editedSummary, setEditedSummary] = useState('');

    // Get summary from profile
    // Priority: structured field (source of truth) > legacy fallbacks
    const summary = profile?.parsedResume?.summary ||
                   profile?.parsedResume?.rawJson?.summary ||
                   profile?.summary ||
                   profile?.resume_data?.parsed_data?.summary ||
                   '';

    // Track dirty state for save button
    const { isDirty, setOriginal, reset: resetDirty } = useDirtyState(editedSummary);

    // Initialize edited summary when entering edit mode
    useEffect(() => {
        if (isEditing) {
            setEditedSummary(summary);
            setOriginal(summary);
        } else {
            resetDirty();
        }
    }, [isEditing, summary]);

    const handleGenerateAI = async () => {
        setIsGenerating(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${DAVID_URL}/candidate-ai/v2/generate-summary`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) throw new Error('Failed to generate summary');
            const data = await res.json();
            setEditedSummary(data.summary || '');
            setIsEditing(true);
        } catch (error) {
            console.error('AI summary generation failed:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedSummary('');
    };

    const handleSave = async () => {
        if (onSave) {
            setIsSaving(true);
            try {
                await onSave(editedSummary);
                setIsEditing(false);
            } catch (error) {
                console.error('Failed to save summary:', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            setIsEditing(false);
        }
    };

    const characterCount = editedSummary.length;
    const isOverLimit = characterCount > MAX_SUMMARY_LENGTH;

    return (
        <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 w-full font-manrope">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <FileText size={20} className="text-[#0B111E]" />
                    <h3 className="text-lg font-semibold text-[#0B111E]">About</h3>
                </div>
                {!isEditing ? (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={handleGenerateAI}
                            disabled={isGenerating}
                            className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors disabled:opacity-50"
                            title="Generate with AI"
                        >
                            {isGenerating ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
                        </button>
                        <button
                            onClick={handleEdit}
                            className="text-[#015FA6] hover:bg-blue-50 p-2 rounded-full transition-colors"
                        >
                            <PenSquare size={20} />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors"
                            disabled={isSaving}
                        >
                            <X size={20} />
                        </button>
                        <button
                            onClick={handleSave}
                            className={`p-2 rounded-full transition-colors ${
                                isDirty && !isOverLimit ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 cursor-not-allowed'
                            }`}
                            disabled={isSaving || isOverLimit || !isDirty}
                        >
                            <Check size={20} />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <div>
                    <textarea
                        value={editedSummary}
                        onChange={(e) => setEditedSummary(e.target.value)}
                        placeholder="Write a brief professional summary about yourself..."
                        rows={5}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none resize-none ${
                            isOverLimit
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-200 focus:border-[#015FA6]'
                        }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                        <button
                            onClick={handleGenerateAI}
                            disabled={isGenerating}
                            className="flex items-center gap-1 text-xs text-[#015FA6] hover:text-[#014a85] disabled:opacity-50"
                        >
                            {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            {isGenerating ? 'Generating...' : 'Regenerate with AI'}
                        </button>
                        <span className={`text-xs ${
                            isOverLimit ? 'text-red-500' : 'text-[#A5A5A5]'
                        }`}>
                            {characterCount}/{MAX_SUMMARY_LENGTH}
                        </span>
                    </div>
                </div>
            ) : (
                <div>
                    {summary ? (
                        <p className="text-sm text-[#515151] leading-relaxed whitespace-pre-wrap">
                            {summary}
                        </p>
                    ) : (
                        <div className="text-center py-4">
                            <div className="w-12 h-12 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-2">
                                <FileText size={20} className="text-[#A5A5A5]" />
                            </div>
                            <p className="text-sm text-[#757575]">No summary added yet</p>
                            <p className="text-xs text-[#A5A5A5]">Click edit to add a professional summary</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AboutCard;
